/**
 * Step 3: Parse CV(s)
 *
 * Single mode: upload + parse one file, auto-advance on success.
 * Bulk mode: sequentially parse each file with progress tracking.
 */

import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
} from "@mui/icons-material";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { recruitingAPI } from "../../services/recruitingApi";
import type { WizardState, BulkFileResult } from "./CandidateImportWizard";

interface Props {
  wizardState: WizardState;
  onNext: () => void;
  onBack: () => void;
  updateState: (updates: Partial<WizardState>) => void;
}

const Step3_Parse: React.FC<Props> = ({
  wizardState,
  onNext,
  onBack,
  updateState,
}) => {
  const { t } = useTranslation("recruiting");
  const isBulk = wizardState.importMethod === "bulk-cv";

  // Single mode state
  const [parseStatus, setParseStatus] = useState<
    "parsing" | "success" | "error"
  >("parsing");

  // Bulk mode state
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [bulkResults, setBulkResults] = useState<BulkFileResult[]>([]);
  const [bulkDone, setBulkDone] = useState(false);
  const cancelledRef = useRef(false);

  // Single mode mutation (existing behavior)
  const uploadMutation = useMutation(
    (formData: FormData) => recruitingAPI.uploadSingleCV(formData),
    {
      onSuccess: (response) => {
        if (response.success) {
          setParseStatus("success");
          updateState({
            parsedCandidate: response.data,
            fileId: response.data?._fileId,
            parseError: null,
          });
          setTimeout(() => onNext(), 1500);
        } else {
          setParseStatus("error");
          updateState({
            parseError: response.error || "AI extraction failed",
            rawText: response.rawText,
            parsedCandidate: null,
          });
        }
      },
      onError: (error: any) => {
        setParseStatus("error");
        updateState({
          parseError: error.message || "Upload failed",
          parsedCandidate: null,
        });
      },
    },
  );

  // Single mode: trigger on mount
  useEffect(() => {
    if (!isBulk) {
      const formData = new FormData();
      formData.append("cv", wizardState.uploadedFile!);
      uploadMutation.mutate(formData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track latest results in a ref to avoid stale closures
  const bulkResultsRef = useRef<BulkFileResult[]>([]);

  // Bulk mode: process files sequentially
  useEffect(() => {
    if (!isBulk || !wizardState.uploadedFiles?.length) return;

    const files = wizardState.uploadedFiles;
    setBulkProgress({ current: 0, total: files.length });
    cancelledRef.current = false;

    const processFiles = async () => {
      const results: BulkFileResult[] = [];

      for (let i = 0; i < files.length; i++) {
        if (cancelledRef.current) break;

        setBulkProgress({ current: i, total: files.length });

        // Update current file as processing
        const processingResult: BulkFileResult = {
          index: i,
          fileName: files[i].name,
          status: "processing",
          included: false,
        };
        setBulkResults([...results, processingResult]);

        try {
          const formData = new FormData();
          formData.append("cv", files[i]);
          const response = await recruitingAPI.uploadSingleCV(formData);

          if (response.success) {
            results.push({
              index: i,
              fileName: files[i].name,
              status: "success",
              parsedCandidate: response.data,
              fileId: response.data?._fileId,
              included: true,
            });
          } else {
            results.push({
              index: i,
              fileName: files[i].name,
              status: "error",
              error: response.error || "AI extraction failed",
              rawText: response.rawText,
              included: false,
            });
          }
        } catch (error: any) {
          results.push({
            index: i,
            fileName: files[i].name,
            status: "error",
            error: error.message || "Upload failed",
            included: false,
          });
        }

        bulkResultsRef.current = [...results];
        setBulkResults(bulkResultsRef.current);
      }

      setBulkProgress({ current: files.length, total: files.length });
      setBulkDone(true);
      updateState({ bulkResults: results });
    };

    processFiles().catch((err) => {
      console.error("Bulk processing error:", err);
      setBulkDone(true);
      updateState({ bulkResults: bulkResultsRef.current });
    });

    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    cancelledRef.current = true;
    setBulkDone(true);
    updateState({ bulkResults: bulkResultsRef.current });
  };

  const handleContinue = () => {
    onNext();
  };

  // Bulk mode render
  if (isBulk) {
    const successCount = bulkResults.filter(
      (r) => r.status === "success",
    ).length;
    const errorCount = bulkResults.filter((r) => r.status === "error").length;
    const progressPercent =
      bulkProgress.total > 0
        ? (bulkProgress.current / bulkProgress.total) * 100
        : 0;
    const currentFile = wizardState.uploadedFiles?.[bulkProgress.current];

    return (
      <Box sx={{ py: 2 }}>
        {!bulkDone ? (
          <>
            <Typography variant="h5" gutterBottom>
              {t("importWizard.step3.parsingBulkTitle")}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {t("importWizard.step3.processingFile", {
                current: bulkProgress.current + 1,
                total: bulkProgress.total,
              })}
              {currentFile ? `: ${currentFile.name}` : ""}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ height: 8, borderRadius: 4, mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t("importWizard.step3.progressSummary", {
                success: successCount,
                failed: errorCount,
              })}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h5" gutterBottom>
              {t("importWizard.step3.parsingComplete")}
            </Typography>
            <Alert
              severity={
                errorCount === 0
                  ? "success"
                  : successCount > 0
                    ? "warning"
                    : "error"
              }
              sx={{ mb: 3 }}
            >
              {t("importWizard.step3.parseResult", {
                success: successCount,
                total: bulkResults.length,
              })}
              {errorCount > 0 &&
                ` ${t("importWizard.step3.failedCount", { count: errorCount })}`}
              {cancelledRef.current &&
                ` ${t("importWizard.step3.cancelledEarly")}`}
            </Alert>
          </>
        )}

        <List dense sx={{ maxHeight: 300, overflow: "auto", mb: 3 }}>
          {bulkResults.map((result) => (
            <ListItem key={result.index}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {result.status === "success" && (
                  <CheckCircle color="success" fontSize="small" />
                )}
                {result.status === "error" && (
                  <ErrorIcon color="error" fontSize="small" />
                )}
                {result.status === "processing" && (
                  <CircularProgress size={20} />
                )}
                {result.status === "pending" && (
                  <HourglassEmpty color="disabled" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={result.fileName}
                secondary={
                  result.status === "success"
                    ? `${result.parsedCandidate?.first_name} ${result.parsedCandidate?.last_name}`
                    : result.status === "error"
                      ? result.error
                      : result.status === "processing"
                        ? t("importWizard.step3.processing")
                        : t("importWizard.step3.waiting")
                }
              />
            </ListItem>
          ))}
        </List>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          {!bulkDone ? (
            <>
              <Box />
              <Button variant="outlined" color="warning" onClick={handleCancel}>
                {t("importWizard.step3.cancelRemaining")}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onBack} variant="outlined">
                {t("importWizard.step3.back")}
              </Button>
              <Button
                variant="contained"
                onClick={handleContinue}
                disabled={successCount === 0}
              >
                {t("importWizard.step3.nextReview", { count: successCount })}
              </Button>
            </>
          )}
        </Box>
      </Box>
    );
  }

  // Single mode render (existing behavior)
  return (
    <Box sx={{ textAlign: "center", py: 4 }}>
      {parseStatus === "parsing" && (
        <>
          <CircularProgress
            size={80}
            sx={{ mb: 3 }}
            data-testid="parsing-spinner"
          />
          <Typography variant="h5" gutterBottom>
            {t("importWizard.step3.parsingSingleTitle")}
          </Typography>
          <Typography color="text.secondary">
            {t("importWizard.step3.extractingFrom", {
              name: wizardState.uploadedFile?.name,
            })}
          </Typography>
        </>
      )}

      {parseStatus === "success" && (
        <>
          <Typography
            variant="h5"
            color="success.main"
            gutterBottom
            data-testid="parsing-complete"
          >
            {t("importWizard.step3.parsingComplete")}
          </Typography>
          <Typography color="text.secondary">
            {t("importWizard.step3.foundCandidate", {
              name: `${wizardState.parsedCandidate?.first_name} ${wizardState.parsedCandidate?.last_name}`,
            })}
          </Typography>
        </>
      )}

      {parseStatus === "error" && (
        <>
          <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
            <Typography variant="h6" gutterBottom>
              {t("importWizard.step3.extractionFailed")}
            </Typography>
            <Typography variant="body2">{wizardState.parseError}</Typography>
          </Alert>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("importWizard.step3.manualEntryHint")}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button onClick={onBack} variant="outlined">
              {t("importWizard.step3.back")}
            </Button>
            <Button variant="contained" onClick={onNext}>
              {t("importWizard.step3.continueManual")}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Step3_Parse;
